/**
 * "We'll be right back" when the server can't be reached.
 *
 * During a restart or an outage, requests used to fail with a generic
 * "Service Unavailable" toast. Now, when the API client reports a suspicious
 * failure, this confirms with two /health probes and — only if both fail —
 * covers the app with the maintenance screen, re-checks every few seconds,
 * and once the server answers again offers a reload (it does not reload by
 * itself, so nothing typed is lost).
 *
 * Being offline (the user's own connection) is a separate, non-blocking
 * banner: that isn't our outage.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Maintenance from '../pages/Maintenance';
import { onPossibleOutage, probeHealth } from '../utils/serverHealth';
import './ServerOutageGuard.css';

export const RECHECK_MS = 5000;
const CONFIRM_DELAY_MS = 2000;

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export default function ServerOutageGuard() {
  const [down, setDown] = useState(false);
  const [recovered, setRecovered] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  const checking = useRef(false);
  const downRef = useRef(false);

  const markDown = (value) => { downRef.current = value; setDown(value); };

  const confirmOutage = useCallback(async () => {
    if (checking.current || downRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    checking.current = true;
    try {
      if (await probeHealth()) return;
      await wait(CONFIRM_DELAY_MS);
      if (await probeHealth()) return;
      setRecovered(false);
      markDown(true);
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => onPossibleOutage(confirmOutage), [confirmOutage]);

  // While down, re-check until the server answers.
  useEffect(() => {
    if (!down) return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      if (cancelled || checking.current) return;
      checking.current = true;
      const ok = await probeHealth();
      checking.current = false;
      if (ok && !cancelled) {
        markDown(false);
        setRecovered(true);
      }
    }, RECHECK_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [down]);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  const tryNow = async () => {
    if (await probeHealth()) {
      markDown(false);
      setRecovered(true);
    }
  };

  return (
    <>
      {down && (
        <div className="server-outage-overlay" role="alertdialog" aria-modal="true" aria-label="Canira is temporarily unavailable">
          <Maintenance
            title="We'll be right back"
            message="Canira is restarting or briefly unavailable — usually a minute or two. This page checks again every few seconds and lets you know when we're back."
            showCountdown={false}
          />
          <div className="server-outage-actions">
            <button type="button" className="server-outage-btn" onClick={tryNow}>Try again now</button>
          </div>
        </div>
      )}
      {!down && recovered && (
        <div className="server-outage-banner server-outage-banner--ok" role="status">
          <span>We&apos;re back online. Reload to make sure everything is up to date.</span>
          <span className="server-outage-banner-actions">
            <button type="button" className="server-outage-btn" onClick={() => window.location.reload()}>Reload</button>
            <button type="button" className="server-outage-btn server-outage-btn--ghost" onClick={() => setRecovered(false)}>Dismiss</button>
          </span>
        </div>
      )}
      {!down && offline && (
        <div className="server-outage-banner server-outage-banner--offline" role="status">
          You&apos;re offline. Check your internet connection — Canira will work again once you&apos;re reconnected.
        </div>
      )}
    </>
  );
}
